import { SwapStatus } from '@layerswap/widget-types';
import { BackendTransactionStatus, TransactionStatus, TransactionType, } from '../../lib/apiClients/layerSwapApiClient';
import { SwapFailReasons } from '../../Models/RangeError';
import { Progress, ProgressStatus } from '../Pages/Swap/Withdraw/Processing/types';
import { formatElapsedHms, msToParts } from './formatTime';
import { SwapPhase, TERMINAL_PHASES } from './swapPhase';
export { SwapPhase, TERMINAL_PHASES };
export function resolveSwapPhase(input) {
    const { swapDetails, refuel, gaslessFailureStatus } = input;
    const inputTxStatusFromApi = input.atomicBatchFailed ? TransactionStatus.Failed : input.inputTxStatusFromApi;
    const depositSubmitted = !!input.depositCompleted
        || !!input.providerSubmissionAccepted
        || inputTxStatusFromApi === TransactionStatus.Pending
        || inputTxStatusFromApi === TransactionStatus.Completed;
    const inputTx = swapDetails?.transactions?.find(t => t.type === TransactionType.Input);
    const outputTx = swapDetails?.transactions?.find(t => t.type === TransactionType.Output);
    const refuelTx = swapDetails?.transactions?.find(t => t.type === TransactionType.Refuel);
    const swapStatus = swapDetails?.status;
    const inputReady = !!(inputTx && inputTx.confirmations >= inputTx.max_confirmations);
    const outputReady = !!(outputTx?.transaction_hash && outputTx?.amount);
    const refuelReady = !!(refuelTx?.transaction_hash && refuelTx?.amount);
    const refuelPending = !!refuel && !refuelReady;
    const swapInputTxStatus = resolveSwapInputTxStatus(inputTx, inputTxStatusFromApi, gaslessFailureStatus);
    // Only client-detected failures (no API input tx) are retryable: retry() re-opens the
    // withdraw screen, which requires no input tx to be listed.
    const failureReason = gaslessFailureStatus
        ? 'gasless_deposit_failed'
        : (!inputTx && swapInputTxStatus === TransactionStatus.Failed) ? 'transfer_failed' : undefined;
    const showWithdrawScreen = (!swapStatus || swapStatus === SwapStatus.UserTransferPending || swapStatus === SwapStatus.Created)
        && !(inputTx || depositSubmitted)
        && !failureReason;
    const phase = resolvePhase({
        swapStatus,
        swapInputTxStatus,
        inputReady,
        outputReady,
        refuelPending,
        hasInputTx: !!inputTx,
        depositSubmitted,
        showWithdrawScreen,
    });
    const stepStatuses = resolveStepStatuses({
        phase,
        swapStatus,
        swapInputTxStatus,
        inputTx,
        outputTx,
        refuelTx,
        inputReady,
        outputReady,
        refuel,
    });
    const generalStatus = resolveGeneralStatus({
        phase,
        inputTx,
        outputTx,
        inputReady,
        failReason: swapDetails?.fail_reason,
        isDepositFlow: input.isDepositFlow ?? false,
    });
    const isTerminal = TERMINAL_PHASES.has(phase);
    const isRefundFlow = phase === SwapPhase.PendingRefund || phase === SwapPhase.Refunded;
    const keepWalletControls = !!input.walletActionExecuting && !isRefundFlow
        && (!isTerminal || phase === SwapPhase.Completed);
    const hidesSteps = phase === SwapPhase.Expired;
    const showsFailedPanel = phase === SwapPhase.Expired;
    // Backend completion can precede output/refuel delivery; keep timing until the resolved flow ends.
    const showsEstimatedTime = !isTerminal && phase !== SwapPhase.PendingRefund;
    return {
        phase,
        stepStatuses,
        generalStatus,
        isTerminal,
        inputReady,
        outputReady,
        showWithdrawScreen: showWithdrawScreen || keepWalletControls,
        swapInputTxStatus,
        isRefundFlow,
        hidesSteps,
        showsFailedPanel,
        showsEstimatedTime,
        failureReason,
        gaslessFailureStatus,
    };
}
function resolvePhase(args) {
    const { swapStatus, swapInputTxStatus, inputReady, outputReady, refuelPending, hasInputTx, depositSubmitted, showWithdrawScreen, } = args;
    if (swapStatus === SwapStatus.Expired)
        return SwapPhase.Expired;
    if (swapStatus === SwapStatus.Refunded)
        return SwapPhase.Refunded;
    if (swapStatus === SwapStatus.PendingRefund)
        return SwapPhase.PendingRefund;
    if (swapStatus === SwapStatus.Failed)
        return SwapPhase.Failed;
    if (swapStatus === SwapStatus.Completed) {
        return outputReady && !refuelPending ? SwapPhase.Completed : SwapPhase.SettlingOutput;
    }
    if (outputReady)
        return refuelPending ? SwapPhase.SettlingOutput : SwapPhase.Completed;
    if (swapInputTxStatus === TransactionStatus.Failed)
        return SwapPhase.Failed;
    if (showWithdrawScreen)
        return SwapPhase.AwaitingUserDeposit;
    if (swapStatus === SwapStatus.LsTransferPending)
        return SwapPhase.OutputPending;
    if (inputReady)
        return SwapPhase.OutputPending;
    if (hasInputTx || depositSubmitted)
        return SwapPhase.InputPending;
    return SwapPhase.AwaitingUserDeposit;
}
function resolveStepStatuses(args) {
    const { phase, swapStatus, swapInputTxStatus, refuelTx, inputReady, outputReady, refuel } = args;
    let input_transfer = transactionStatusToProgressStatus(swapInputTxStatus);
    let output_transfer = outputReady
        ? ProgressStatus.Complete
        : inputReady ? ProgressStatus.Current : ProgressStatus.Upcoming;
    const refuelReady = !!(refuelTx?.transaction_hash && refuelTx?.amount);
    let refuel_transfer = refuelReady
        ? ProgressStatus.Complete
        : refuel ? ProgressStatus.Upcoming : ProgressStatus.Removed;
    let refund = ProgressStatus.Removed;
    switch (phase) {
        case SwapPhase.PendingRefund:
        case SwapPhase.Refunded:
            input_transfer = ProgressStatus.Complete;
            output_transfer = ProgressStatus.Failed;
            refuel_transfer = ProgressStatus.Removed;
            refund = phase === SwapPhase.Refunded ? ProgressStatus.Complete : ProgressStatus.Current;
            break;
        case SwapPhase.Failed:
            if (swapInputTxStatus === TransactionStatus.Failed) {
                input_transfer = ProgressStatus.Failed;
                if (swapStatus === SwapStatus.Failed && output_transfer !== ProgressStatus.Complete) {
                    output_transfer = ProgressStatus.Failed;
                }
            }
            else if (output_transfer !== ProgressStatus.Complete) {
                output_transfer = ProgressStatus.Failed;
            }
            if (refuel_transfer !== ProgressStatus.Complete) {
                refuel_transfer = ProgressStatus.Removed;
            }
            break;
        case SwapPhase.SettlingOutput:
            input_transfer = ProgressStatus.Complete;
            output_transfer = outputReady ? ProgressStatus.Complete : ProgressStatus.Current;
            if (outputReady && refuel && !refuelReady)
                refuel_transfer = ProgressStatus.Current;
            break;
        case SwapPhase.Completed:
            input_transfer = ProgressStatus.Complete;
            output_transfer = ProgressStatus.Complete;
            break;
        default:
            break;
    }
    return {
        [Progress.InputTransfer]: input_transfer,
        [Progress.OutputTransfer]: output_transfer,
        [Progress.Refuel]: refuel_transfer,
        [Progress.Refund]: refund,
    };
}
function resolveGeneralStatus(args) {
    const { phase, inputTx, outputTx, inputReady, failReason, isDepositFlow } = args;
    const noun = isDepositFlow ? 'Deposit' : 'Transfer';
    const lowerNoun = isDepositFlow ? 'deposit' : 'transfer';
    switch (phase) {
        case SwapPhase.Completed:
            return { title: `${noun} complete`, subTitle: formatElapsedTime(inputTx, outputTx) };
        case SwapPhase.SettlingOutput:
            return { title: `Finalizing ${lowerNoun}`, subTitle: null };
        case SwapPhase.Refunded:
            return {
                title: 'Refund complete',
                subTitle: 'We couldn’t complete your transaction. The full amount has been returned to your wallet.',
            };
        case SwapPhase.PendingRefund:
            return {
                title: 'Processing refund',
                subTitle: 'Your transaction could not be processed. The full amount will be returned to your wallet.',
            };
        case SwapPhase.Failed:
            return {
                title: failReason === SwapFailReasons.RECEIVED_MORE_THAN_VALID_RANGE ? `${noun} on hold` : `${noun} failed`,
                subTitle: 'View instructions below',
            };
        case SwapPhase.Expired:
            return { title: `${noun} expired`, subTitle: '...' };
        default:
            return { title: `${noun} in progress`, subTitle: inputReady ? '' : null };
    }
}
const BACKEND_TO_TX_STATUS = {
    [BackendTransactionStatus.Completed]: TransactionStatus.Completed,
    [BackendTransactionStatus.Failed]: TransactionStatus.Failed,
    [BackendTransactionStatus.Pending]: TransactionStatus.Pending,
    [BackendTransactionStatus.Initiated]: TransactionStatus.Pending,
};
function resolveSwapInputTxStatus(swapInputTransaction, inputTxStatusFromApi, gaslessFailureStatus) {
    if (swapInputTransaction) {
        if (swapInputTransaction.status === BackendTransactionStatus.Completed
            && swapInputTransaction.confirmations < swapInputTransaction.max_confirmations) {
            return TransactionStatus.Pending;
        }
        return BACKEND_TO_TX_STATUS[swapInputTransaction.status];
    }
    if (inputTxStatusFromApi === TransactionStatus.Failed)
        return inputTxStatusFromApi;
    // Gasless deposit failed terminally with no input tx published.
    if (gaslessFailureStatus)
        return TransactionStatus.Failed;
    return TransactionStatus.Pending;
}
function transactionStatusToProgressStatus(transactionStatus) {
    switch (transactionStatus) {
        case TransactionStatus.Completed:
            return ProgressStatus.Complete;
        case TransactionStatus.Failed:
            return ProgressStatus.Failed;
        case TransactionStatus.Pending:
            return ProgressStatus.Current;
        default:
            return ProgressStatus.Upcoming;
    }
}
function formatElapsedTime(inputTx, outputTx) {
    const start = inputTx?.timestamp || inputTx?.created_date;
    const end = outputTx?.timestamp || outputTx?.created_date;
    if (!start || !end)
        return null;
    const diffMs = new Date(end).getTime() - new Date(start).getTime();
    if (!Number.isFinite(diffMs) || diffMs < 0)
        return null;
    // Atomic token swaps can record input and output in the same transaction.
    // A zero timestamp difference is valid, not a missing completion duration.
    return `Completed in ${diffMs < 1000 ? '<1s' : formatElapsedHms(msToParts(diffMs))}`;
}
